import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { userSelector } from '@src/selectors/app'
import {
  appointmentRecurrenceSelector,
  insurancePolicySelector,
} from '@src/screens/NutritionistHub/models/nutritionistHub.selectors'
import { RootStackParamList } from '@src/navigation/types'
import { UserPropertyOnboardingExperienceKind } from '@src/types'
import { buildStepsConfig } from './utils'
import { ContextData, StepConfig } from './types'

// We want to simulate a loading time for the user to see the loading screen
// To feel that we are creating the personal plan for the user
const FAKE_LOADING_TOTAL_TIME = 3000
const CREATING_PLAN_SCREEN_INDEX = 0
const PLAN_DETAILS_SCREEN_INDEX = 1

export const WeightLossExperienceContext = createContext({} as ContextData)
const WeightLossExperienceProvider = ({ children }: { children: ReactNode }) => {
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>()

  const user = useSelector(userSelector)
  const appointmentRecurrence = useSelector(appointmentRecurrenceSelector)
  const insurancePolicy = useSelector(insurancePolicySelector)

  const [fakeLoading, setFakeLoading] = useState(true)
  const [currentStepIndex, setCurrentStepIndex] = useState(CREATING_PLAN_SCREEN_INDEX)

  // Only users in the weight loss experience can update their plan
  const appointmentRecurrenceExists = !!appointmentRecurrence
  const userIsUpdatingTheirPlan =
    appointmentRecurrenceExists &&
    user?.onboardingExperience?.kind === UserPropertyOnboardingExperienceKind.WeightLoss
  const insurancePolicyIsEligible = !!(insurancePolicy && !insurancePolicy.declined)

  const stepsConfig = useMemo(() => {
    return buildStepsConfig({
      user,
      userIsUpdatingTheirPlan,
      insurancePolicyIsEligible,
      appointmentRecurrenceExists,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insurancePolicyIsEligible])

  useEffect(() => {
    setTimeout(() => {
      setFakeLoading(false)
      setCurrentStepIndex(PLAN_DETAILS_SCREEN_INDEX)
    }, FAKE_LOADING_TOTAL_TIME)
  }, [])

  const goToNextStep = () => {
    if (userIsUpdatingTheirPlan || currentStepIndex === stepsConfig.length - 1) {
      setCurrentStepIndex(CREATING_PLAN_SCREEN_INDEX)
      navigation.replace('Drawer', { screen: 'Dashboard' })
      return
    }

    setCurrentStepIndex((prev) => prev + 1)
  }

  const goToPreviousStep = () => {
    if (currentStepIndex === CREATING_PLAN_SCREEN_INDEX) {
      return
    }

    setCurrentStepIndex((prev) => prev - 1)
  }

  const stepConfig = useMemo(() => {
    return stepsConfig[currentStepIndex] as StepConfig | undefined
  }, [stepsConfig, currentStepIndex])

  const showBackButton =
    currentStepIndex > CREATING_PLAN_SCREEN_INDEX && currentStepIndex < stepsConfig.length - 1

  return (
    <WeightLossExperienceContext.Provider
      value={{
        isLoading: fakeLoading,
        goToNextStep,
        goToPreviousStep,
        currentStepIndex,
        fakeLoadingTotalTime: FAKE_LOADING_TOTAL_TIME,
        showBackButton,
        stepConfig,
        insurancePolicyIsEligible,
        onboardingExperience: user?.onboardingExperience,
        appointmentRecurrenceExists,
      }}
    >
      {children}
    </WeightLossExperienceContext.Provider>
  )
}

const useWeightLossExperience = (): ContextData => {
  const context = useContext(WeightLossExperienceContext)

  return context
}

export { WeightLossExperienceProvider, useWeightLossExperience }
